<template>
  <QueryLoading v-if="isCategoriesLoading" />
  <QueryError v-else-if="isCategoriesError" class="h-full">
    Failed to retrieve categories
  </QueryError>
  <div v-else v-bind="containerProps" class="h-full p-3">
    <div v-bind="wrapperProps">
      <template
        v-for="item in renderedItems"
        :key="item.index"
      >
        <template v-if="item.data.type === 'searchInput'">
          <div class="flex flex-col px-3">
            <SearchInput v-model="searchString" />
          </div>
        </template>
        <template v-if="item.data.type === 'category'">
          <slot name="category" v-bind="item.data" />
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Category } from "@/services/sharedTypes/categories"
import { useCategories } from "@/services/categories/listCategories"
import { useVirtualList } from "@vueuse/core"
import type { DeepReadonly } from "vue"

const {
  data: categories,
  isError: isCategoriesError,
  isLoading: isCategoriesLoading,
} = useCategories()

type VirtualListItemCategory = {
  type: "category"
  id: string
  category: DeepReadonly<Category>
}

type VirtualListItemSearch = {
  type: "searchInput"
}

type VirtualListItem =
  | VirtualListItemCategory
  | VirtualListItemSearch

const searchString = ref<string>("")

const virtualList = computed<VirtualListItem[]>(() => {
  const filteredCategories =
    categories.value?.filter(
      (category) =>
        category.name
          .toLowerCase()
          .includes(searchString.value.toLowerCase()) ||
        category.namePath
          .toLowerCase()
          .includes(searchString.value.toLowerCase())
    ) ?? []

  const categoriesList: VirtualListItemCategory[] =
    filteredCategories.map((category) => ({
      type: "category",
      id: `category:${category.id}`,
      category: category,
    }))

  return [{ type: "searchInput" }, ...categoriesList]
})

const calculateItemHeight = (index: number) => {
  const searchInputHeight = 38
  const containerPadding = 12
  const type = virtualList.value[index].type
  switch (type) {
    case "category":
      return 80
    case "searchInput":
      // Adjustment required for scrollTo(1) to work
      return searchInputHeight + containerPadding
    default:
      return 0
  }
}

const {
  list: renderedItems,
  containerProps,
  wrapperProps,
  scrollTo,
} = useVirtualList(virtualList, {
  itemHeight: calculateItemHeight,
  overscan: 5,
})

onMounted(() => {
  // Initially hide search bar
  scrollTo(1)
})
</script>
